import React, { useEffect, useState } from "react";
import Modal from "./Modal.tsx";
import StyledButton from "./Button.tsx";
import {
  ModalInput,
  ModalLabel,
  ModalButtonRow,
  CancelButton,
  SmallText,
  ErrorMessage,
} from "./ModalFormComponents.tsx";
import { updateHierarchyNode } from "../services/categories.service.ts";
import { parseApiError } from "../utils/errorHandler.ts";

interface EditFolderModalProps {
  isOpen: boolean;
  onClose: () => void;
  categoryId: number;
  nodeId: number;
  categoryType?: string;
  initialName: string;
  initialDescription?: string;
  onSuccess?: () => void;
}

export default function EditFolderModal({
  isOpen,
  onClose,
  categoryId,
  nodeId,
  categoryType,
  initialName,
  initialDescription = "",
  onSuccess,
}: EditFolderModalProps) {
  const [folderName, setFolderName] = useState("");
  const [folderDescription, setFolderDescription] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      setFolderName(initialName);
      setFolderDescription(initialDescription ?? "");
      setError(null);
    }
  }, [isOpen, initialName, initialDescription]);

  const handleClose = () => {
    setError(null);
    onClose();
  };

  const handleSave = async () => {
    setError(null);
    if (!folderName.trim()) {
      setError("Mappe navn er påkrævet");
      return;
    }

    setSaving(true);
    try {
      await updateHierarchyNode(
        categoryId,
        nodeId,
        {
          name: folderName.trim(),
          description: folderDescription.trim(),
        },
        categoryType
      );
      onSuccess?.();
      handleClose();
    } catch (err: unknown) {
      setError(parseApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Rediger mappe">
      <SmallText>Opdater navn og beskrivelse for mappen.</SmallText>
      {error && <ErrorMessage>{error}</ErrorMessage>}

      <ModalLabel>Mappe navn *</ModalLabel>
      <ModalInput
        placeholder="f.eks. Manualer"
        value={folderName}
        onChange={(e) => setFolderName(e.target.value)}
        style={{ marginBottom: "0.75rem" }}
      />

      <ModalLabel>Beskrivelse</ModalLabel>
      <ModalInput
        placeholder="Beskrivelse af mappen (valgfrit)"
        value={folderDescription}
        onChange={(e) => setFolderDescription(e.target.value)}
      />

      <ModalButtonRow>
        <CancelButton onClick={handleClose} disabled={saving}>
          Annuller
        </CancelButton>
        <StyledButton onClick={handleSave} disabled={saving}>
          {saving ? "Gemmer..." : "Gem"}
        </StyledButton>
      </ModalButtonRow>
    </Modal>
  );
}
